import { backUrl } from "../utils/backURL";
import { apiFetch } from "../utils/apiFetch";

/**
 * Calls Perlita API through backend proxy to avoid CORS issues
 * @param {string} prompt - The prompt/instruction to send to Perlita
 * @param {Array} messages - Array of conversation messages
 * @param {Object} options - Additional options (model, temperature, system_instructions)
 * @returns {Promise<Object>} The response from Perlita
 */
export const callPerlita = async (prompt, messages = [], options = {}) => {
  try {
    // Ensure models is always sent as an array
    // The API expects "models" (plural) not "model" (singular)
    const modelValue = options.model || "us.anthropic.claude-haiku-4-5-20251001-v1:0";
    const modelArray = Array.isArray(modelValue) ? modelValue : [modelValue];
    
    const perlaData = {
      models: modelArray, // API expects "models" (plural)
      prompt: prompt,
      temperature: options.temperature || 0.7,
      system_instructions: options.system_instructions || "",
      ...(messages.length > 0 && { messages: messages }),
      ...(options.files && { files: options.files }),
    };

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 60000);

    // Call backend proxy endpoint instead of directly calling Perlita
    const response = await apiFetch(`${backUrl}/perlita/process`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(options.token && { Authorization: `Bearer ${options.token}` }),
      },
      body: JSON.stringify(perlaData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error calling Perlita:", error);
    if (error.name === "AbortError") {
      throw new Error("Request timeout - el análisis tomó demasiado tiempo");
    }
    throw error;
  }
};

/**
 * Formats conversation messages for Perlita API
 * @param {Array} messages - Array of message objects from the conversation
 * @returns {Array} Formatted messages array for Perlita
 */
export const formatMessagesForPerlita = (messages) => {
  return messages
    .sort((a, b) => {
      const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return dateA - dateB;
    })
    .map((message) => {
      const role = message.sender === "USER" ? "user" : 
                   message.sender === "AGENT" ? "assistant" : 
                   message.sender === "MANTIS" ? "assistant" : "user";
      
      const content = message.content?.text || 
                     message.content?.html || 
                     "Sin contenido";

      return {
        role: role,
        content: content,
      };
    });
};

/**
 * Formats a conversation as a single text string for analysis
 * @param {Array} messages - Array of message objects
 * @returns {string} Formatted conversation text
 */
export const formatConversationAsText = (messages) => {
  const sortedMessages = messages
    .sort((a, b) => {
      const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return dateA - dateB;
    });

  return sortedMessages
    .map((message, index) => {
      const sender = message.sender === "USER" ? "Usuario" :
                     message.sender === "AGENT" ? "Agente" :
                     message.sender === "MANTIS" ? "Mantis" :
                     message.sender || "Desconocido";
      
      const timestamp = message.dateCreated
        ? new Date(message.dateCreated).toLocaleString("es-ES")
        : "Sin fecha";
      
      const content = message.content?.text || 
                     message.content?.html || 
                     "Sin contenido";

      return `${index + 1}. [${sender}] (${timestamp}): ${content}`;
    })
    .join("\n\n");
};

